import { useMemo, useState, useEffect, useRef } from "react";
import { useSocket } from "@/context/SocketContext";
import { useWatchdog } from "@/hooks/useWatchdog";
import { GainBadge } from "./GainBadge";

import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Play,
  Square,
  Trash2,
  Activity,
  Target,
  TrendingUp,
  BrainCircuit,
  
  Copy,
  Check,
  Loader2,
  Bolt,
  Settings2,
} from "lucide-react";
import { useApp } from "@/context/AppCtx";
import { useStrategist } from "@/hooks/useStrategist";
import { BUILTIN_SCALPER_PRESET_ID } from "@/lib/algorithmPresets";
import {
  appendPumpPortalTradingWalletHint,
  getEffectivePumpPortalApiKey,
  getPumpPortalTradingWalletPubkey,
} from "@/lib/pumpPortalConfig";
import { BotTradesBook } from "@/components/BotTradesBook";
import { ServerBotTrades } from "@/components/ServerBotTrades";
import { AiCommentaryPanel } from "@/components/AiCommentaryPanel";
import { RunnerRow } from "@/components/NurseryPanel";
import { FlyBrainNurseryHUD } from "@/components/FlyBrainNurseryHUD";
import { usePumpPortalConfigRevision } from "@/hooks/usePumpPortalConfigRevision";
import { usePumpPortalLinkedWalletSol } from "@/hooks/usePumpPortalLinkedWalletSol";
import { formatMcUsdBookDetail } from "@/lib/formatUsd";
import { agentWalletManager } from "@/lib/agentWallet";

// ──────────────────────────────────────────────────────────────
// Inline knob that matches the dark premium aesthetic
// ──────────────────────────────────────────────────────────────
export function AgentKnob({
  label,
  value,
  min,
  max,
  step,
  unit,
  accent = "white",
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit?: string;
  accent?: "white" | "emerald" | "rose" | "sky" | "amber";
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  const accentMap: Record<string, string> = {
    white: "text-white focus:border-purple-500/60 from-purple-600 to-purple-400",
    emerald: "text-emerald-400 focus:border-emerald-500/60 from-emerald-600 to-emerald-400",
    rose: "text-rose-400 focus:border-rose-500/60 from-rose-600 to-rose-400",
    sky: "text-sky-400 focus:border-sky-500/60 from-sky-600 to-sky-400",
    amber: "text-amber-400 focus:border-amber-500/60 from-amber-600 to-amber-400",
  };
  
  return (
    <div className="flex flex-col gap-1 group">
      <div className="flex items-center justify-between">
        <label className="text-[7.5px] font-bold uppercase tracking-widest text-white/40 block">
          {label}
        </label>
        <div className="flex items-center gap-0.5">
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={value}
            disabled={disabled}
            onChange={(e) => {
              const n = Number(e.target.value);
              if (Number.isFinite(n)) onChange(n);
            }}
            className={`w-10 bg-transparent text-right font-mono text-[9px] font-bold tabular-nums outline-none disabled:opacity-40 disabled:cursor-not-allowed ${accentMap[accent].split(' ')[0]}`}
          />
          {unit && (
            <span className="text-[8px] text-white/30 font-bold shrink-0">{unit}</span>
          )}
        </div>
      </div>
      <div className="w-full">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n)) onChange(n); }}
          className="w-full h-[3px] bg-black/60 rounded-lg cursor-pointer"
          style={{ accentColor: accent === 'emerald' ? '#34d399' : accent === 'rose' ? '#fb7185' : accent === 'sky' ? '#38bdf8' : accent === 'amber' ? '#fbbf24' : '#a855f7' }}
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// Main panel
// ──────────────────────────────────────────────────────────────
export function HistoryPanel() {
  const {
    persistedBotTrades,
    chartAnalytics,
    caMintInput,
    selectedAlgoId,
    setSelectedAlgoId,
    tradingMode,
    setTradingMode,
    algoSessionActive,
    setAlgoSessionActive,
    setTradingHalted,
    hardStopTrading,
    scalperLiveBuySol,
    setScalperLiveBuySol,
    scalperUserConfig,
    setScalperUserConfig,
    requestManualSell,
    clearPersistedTrades,
    setCaMintInput,
    setMobileActiveTab,
    autoPilotEnabled,
  } = useApp() as any;

  const { recommendations } = useStrategist();
  const watchdog = useWatchdog();
  const isMobile = useIsMobile();
  const { socket, flyBrainState, serverMomentumWatchlist } = useSocket();

  // ── Defaults: always lock to scalper ──
  useEffect(() => {
    if (!selectedAlgoId || selectedAlgoId !== BUILTIN_SCALPER_PRESET_ID) {
      setSelectedAlgoId(BUILTIN_SCALPER_PRESET_ID);
    }
  }, [selectedAlgoId, setSelectedAlgoId]);

  // ── Auto-start agent when CA is loaded ──
  const prevMint = useRef<string | null>(null);
  useEffect(() => {
    if (
      caMintInput &&
      caMintInput !== prevMint.current &&
      selectedAlgoId === BUILTIN_SCALPER_PRESET_ID
    ) {
      if (tradingMode === "real" && !getEffectivePumpPortalApiKey().trim()) {
        // don't auto-start real if no API key
      } else {
        setTradingHalted(false);
        setAlgoSessionActive(true);
      }
    }
    prevMint.current = caMintInput;
  }, [caMintInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dead-chart rotation: auto-jump instantly to highest-alpha token without countdown delays or shifts ──
  const mintSwitchedAt = useRef(Date.now());
  useEffect(() => {
    mintSwitchedAt.current = Date.now();
  }, [caMintInput]);

  useEffect(() => {
    if (!caMintInput || chartAnalytics.paperScalper?.status === "in_trade") return;
    if (!algoSessionActive && !autoPilotEnabled) return;

    // Grace period: allow 20s for new chart connection & initial trades before testing for dead
    const timeSinceSwitch = Date.now() - mintSwitchedAt.current;
    if (timeSinceSwitch < 20_000) return;

    const lastTrade = chartAnalytics.orderBookLastTradeAt as number | null;
    const conn = chartAnalytics.orderBookConn as string;
    const now = Date.now();
    const silentMs = conn === "open" && lastTrade != null ? now - lastTrade : null;

    const isDead =
      (silentMs != null && silentMs > 90_000) ||
      (conn === "open" && lastTrade == null && timeSinceSwitch >= 25_000);

    if (isDead) {
      const next = recommendations.find((r: any) => r.mint && r.mint !== caMintInput && !r.isDead && !r.isRugged && r.mint.toLowerCase() !== "dmxgg48pyynprxdbkxvtzs1kzojc6g6efme8jaoypump");
      if (next) {
        console.log(`[FlyBrain] Dead chart detected for ${caMintInput} -> Instant rotation to ${next.coin || next.mint}`);
        setCaMintInput(next.mint);
      }
    }
  }, [
    chartAnalytics.orderBookLastTradeAt,
    chartAnalytics.orderBookConn,
    chartAnalytics.paperScalper?.status,
    caMintInput,
    algoSessionActive,
    autoPilotEnabled,
    recommendations,
    setCaMintInput,
  ]);

  // ── FlyBrain Neural logs ──
  const [commentaryLogs, setCommentaryLogs] = useState<string[]>(() => {
    const now = new Date().toLocaleTimeString();
    return [
      `[${now}] 🧠 Brain: FlyBrain Market-Wide Neural OS connected`,
      `[${now}] 📡 Stream: PumpPortal real-time market stream active`,
      `[${now}] 🧠 Connectome: Synchronized · 100 SNN neurons online`,
      `[${now}] 👁️ Vision: Scanning 1s order flow and runner tape...`,
    ];
  });

  const [logFilter, setLogFilter] = useState<"all" | "brain" | "motor" | "trades" | "vision">("all");

  // Fetch initial history and listen to real-time FlyBrain events
  useEffect(() => {
    fetch("/api/flybrain/logs")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const formatted = data.map((item: any) => {
            const timeStr = item.ts ? new Date(item.ts).toLocaleTimeString() : new Date().toLocaleTimeString();
            const raw = item.msg || "";
            if (/^\[.*?\]\s*.*?:/.test(raw)) return raw;
            let cat = "🧠 Brain";
            if (raw.includes("MOTOR") || raw.includes("BUY signal") || raw.includes("SELL signal")) cat = "⚡ Motor";
            else if (raw.includes("PRE-BONDING") || raw.includes("CIRCUIT BREAKER") || raw.includes("Risk") || raw.includes("Dump") || raw.includes("Stress")) cat = "🚨 Risk";
            else if (raw.includes("New token") || raw.includes("excited") || raw.includes("sensory")) cat = "🚀 Sensory";
            else if (raw.includes("Stream") || raw.includes("PumpPortal")) cat = "📡 Stream";
            else if (raw.includes("Trade") || raw.includes("BUY on") || raw.includes("SELL on") || raw.includes("Position") || raw.includes("Pyramid")) cat = "💼 Trade";
            return `[${timeStr}] ${cat}: ${raw}`;
          });
          setCommentaryLogs((prev) => {
            const existing = new Set(prev);
            const merged = [...prev];
            formatted.reverse().forEach((f: string) => {
              if (!existing.has(f)) {
                merged.push(f);
                existing.add(f);
              }
            });
            return merged.slice(-120);
          });
        }
      })
      .catch(() => {});

    if (!socket) return;

    const onFlyBrainLog = (data: { msg: string; ts?: number }) => {
      const timeStr = data.ts ? new Date(data.ts).toLocaleTimeString() : new Date().toLocaleTimeString();
      const raw = data.msg || "";
      let formatted: string;
      if (/^\[.*?\]\s*.*?:/.test(raw)) {
        formatted = raw;
      } else {
        let cat = "🧠 Brain";
        if (raw.includes("MOTOR") || raw.includes("BUY signal") || raw.includes("SELL signal")) cat = "⚡ Motor";
        else if (raw.includes("PRE-BONDING") || raw.includes("CIRCUIT BREAKER") || raw.includes("Risk") || raw.includes("Dump") || raw.includes("Stress")) cat = "🚨 Risk";
        else if (raw.includes("New token") || raw.includes("excited") || raw.includes("sensory")) cat = "🚀 Sensory";
        else if (raw.includes("Stream") || raw.includes("PumpPortal")) cat = "📡 Stream";
        else if (raw.includes("Trade") || raw.includes("BUY on") || raw.includes("SELL on") || raw.includes("Position") || raw.includes("Pyramid")) cat = "💼 Trade";
        formatted = `[${timeStr}] ${cat}: ${raw}`;
      }
      setCommentaryLogs((prev) => {
        if (prev.length > 0 && prev[prev.length - 1] === formatted) return prev;
        return [...prev.slice(-119), formatted];
      });
    };

    const onFlyBrainHistory = (history: any[]) => {
      if (!Array.isArray(history) || history.length === 0) return;
      const formatted = history.map((item: any) => {
        const timeStr = item.ts ? new Date(item.ts).toLocaleTimeString() : new Date().toLocaleTimeString();
        const raw = item.msg || "";
        if (/^\[.*?\]\s*.*?:/.test(raw)) return raw;
        let cat = "🧠 Brain";
        if (raw.includes("MOTOR") || raw.includes("BUY signal") || raw.includes("SELL signal")) cat = "⚡ Motor";
        else if (raw.includes("PRE-BONDING") || raw.includes("CIRCUIT BREAKER") || raw.includes("Risk")) cat = "🚨 Risk";
        else if (raw.includes("New token") || raw.includes("excited") || raw.includes("sensory")) cat = "🚀 Sensory";
        else if (raw.includes("Stream") || raw.includes("PumpPortal")) cat = "📡 Stream";
        else if (raw.includes("Trade") || raw.includes("BUY on") || raw.includes("SELL on") || raw.includes("Position") || raw.includes("Pyramid")) cat = "💼 Trade";
        return `[${timeStr}] ${cat}: ${raw}`;
      });
      setCommentaryLogs((prev) => {
        const existing = new Set(prev);
        const merged = [...prev];
        formatted.reverse().forEach((f: string) => {
          if (!existing.has(f)) {
            merged.push(f);
            existing.add(f);
          }
        });
        return merged.slice(-120);
      });
    };

    const onFlyBrainTrade = (data: { side: string; strength?: number; price?: number; pnl?: number }) => {
      const ts = new Date().toLocaleTimeString();
      const pnlStr = data.pnl != null ? ` · PnL: ${data.pnl > 0 ? "+" : ""}${data.pnl.toFixed(2)}%` : "";
      const formatted = `[${ts}] ⚡ Motor: ${data.side.toUpperCase()} trigger · Synaptic strength: ${data.strength || 3} neurons${pnlStr}`;
      setCommentaryLogs((prev) => [...prev.slice(-119), formatted]);
    };

    const onNewServerTrade = (trade: any) => {
      const ts = new Date().toLocaleTimeString();
      const side = (trade.action || "trade").toUpperCase();
      const sym = trade.symbol || (trade.mint ? trade.mint.slice(0, 6) : "TOKEN");
      const pnlStr = trade.pnlPct != null ? ` · PnL: ${trade.pnlPct >= 0 ? "+" : ""}${(trade.pnlPct * 100).toFixed(1)}%` : "";
      const sizeStr = trade.amountUsd ? ` · $${trade.amountUsd.toFixed(0)}` : trade.solAmount ? ` · ${trade.solAmount.toFixed(2)} SOL` : "";
      const reasonStr = trade.closeReason ? ` · (${trade.closeReason})` : "";
      const formatted = `[${ts}] 💼 Trade: ${side} ${sym}${sizeStr}${pnlStr}${reasonStr}`;
      setCommentaryLogs((prev) => [...prev.slice(-119), formatted]);
    };

    socket.on("fly_brain_log", onFlyBrainLog);
    socket.on("fly_brain_history", onFlyBrainHistory);
    socket.on("fly_brain_trade", onFlyBrainTrade);
    socket.on("server-paper-new-trade", onNewServerTrade);

    return () => {
      socket.off("fly_brain_log", onFlyBrainLog);
      socket.off("fly_brain_history", onFlyBrainHistory);
      socket.off("fly_brain_trade", onFlyBrainTrade);
      socket.off("server-paper-new-trade", onNewServerTrade);
    };
  }, [socket]);

  // Track motor decisions from flyBrainState
  const lastSeenDecisionTsRef = useRef<number>(0);
  useEffect(() => {
    if (!flyBrainState?.motorDecisions?.length) return;
    const latest = flyBrainState.motorDecisions[0];
    if (!latest || latest.ts <= lastSeenDecisionTsRef.current) return;
    lastSeenDecisionTsRef.current = latest.ts;
    const ts = new Date(latest.ts).toLocaleTimeString();
    const formatted = `[${ts}] ⚡ Motor: ${latest.msg}`;
    setCommentaryLogs((prev) => {
      if (prev.some((p) => p.includes(latest.msg))) return prev;
      return [...prev.slice(-119), formatted];
    });
  }, [flyBrainState?.motorDecisions]);

  // Track paperScalper status transitions (ONLY when status actually changes)
  const prevPaperStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const status = chartAnalytics.paperScalper?.status;
    if (!status || status === prevPaperStatusRef.current) return;
    prevPaperStatusRef.current = status;
    const ts = new Date().toLocaleTimeString();
    const tokenLabel = caMintInput ? caMintInput.slice(0, 6) : "focus token";
    const msgs: Record<string, string> = {
      watching: `[${ts}] 👁️ Vision: Scanning 1s order flow for ${tokenLabel} · Waiting for momentum setup`,
      dip: `[${ts}] 📉 Setup: Dip detected on ${tokenLabel} · Aligning with support zone`,
      nearing: `[${ts}] 🔮 Vision: ${tokenLabel} nearing mapped support zone · Arming latch`,
      arming: `[${ts}] 🔒 Security: Catalyst latch ARMED for ${tokenLabel} · Waiting for whale buy >= ${scalperUserConfig.catalystMinSol} SOL`,
      in_trade: `[${ts}] 💼 Position: Trade entered on ${tokenLabel} · Monitoring profit/stop gates`,
    };
    const msg = msgs[status];
    if (msg) setCommentaryLogs((p) => [...p.slice(-119), msg]);
  }, [chartAnalytics.paperScalper?.status, caMintInput, scalperUserConfig.catalystMinSol]);

  // ── Real data log: fires when tapeSummary or candle actually updates ──
  const prevTapeSummaryRef = useRef<any>(null);
  const prevCandleRef = useRef<any>(null);
  useEffect(() => {
    if (!algoSessionActive) return;
    const tape = chartAnalytics.tapeSummary;
    const candle = chartAnalytics.lastCandle;
    const ts = new Date().toLocaleTimeString();
    const logs: string[] = [];

    // Emit candle data if the candle changed
    if (candle && candle !== prevCandleRef.current) {
      prevCandleRef.current = candle;
      const dir = candle.close >= candle.open ? "▲" : "▼";
      const pct = candle.open > 0 ? (((candle.close - candle.open) / candle.open) * 100).toFixed(2) : "—";
      logs.push(
        `[${ts}] 🕯️ Candle: ${dir} O:${candle.open.toFixed(4)} H:${candle.high.toFixed(4)} L:${candle.low.toFixed(4)} C:${candle.close.toFixed(4)} (${pct}%) [${candle.interval}]`
      );
    }

    // Emit tape summary ONLY if actual trading activity occurred and changed (no 0-volume empty spam)
    if (tape) {
      const vol = tape.solVolume ?? 0;
      const totalTrades = (tape.buyCount ?? 0) + (tape.sellCount ?? 0);
      const prev = prevTapeSummaryRef.current;
      
      const hasRealTrades = totalTrades > 0 || vol > 0.01;
      const hasChanged = !prev || 
        prev.buyCount !== tape.buyCount || 
        prev.sellCount !== tape.sellCount || 
        Math.abs((prev.solVolume ?? 0) - vol) > 0.01;

      if (hasRealTrades && hasChanged) {
        prevTapeSummaryRef.current = tape;
        const buyPct = tape.buyPct != null ? tape.buyPct.toFixed(0) : "?";
        const volStr = vol.toFixed(3);
        const mc = tape.latestMcUsd != null
          ? tape.latestMcUsd >= 1_000_000
            ? `$${(tape.latestMcUsd / 1_000_000).toFixed(2)}M`
            : `$${(tape.latestMcUsd / 1_000).toFixed(1)}k`
          : "—";
        const pressure = tape.buyPct >= 70 ? "🟢 Strong buy" : tape.buyPct >= 55 ? "Slight buy" : tape.buyPct < 35 ? "🔴 Sell pressure" : "Neutral";
        logs.push(
          `[${ts}] 📊 Flow: ${tape.buyCount}B/${tape.sellCount}S · Vol ${volStr} SOL · Buy% ${buyPct}% · MC ${mc} · ${pressure}`
        );

        // Add rug gate check if large sell detected in recent prints
        const bigSell = tape.recentPrints?.find((p: any) => !p.buy && p.sol >= scalperUserConfig.minOrderBookSellSolForStop);
        if (bigSell) {
          logs.push(
            `[${ts}] 🚨 Whale Dump: Large sell detected — ${bigSell.sol.toFixed(2)} SOL. Monitoring rug stop trigger.`
          );
        }
      }
    }

    if (logs.length > 0) {
      setCommentaryLogs((prev) => {
        const next = [...prev, ...logs];
        return next.length > 120 ? next.slice(-120) : next;
      });
    }
  }, [chartAnalytics.tapeSummary, chartAnalytics.lastCandle, algoSessionActive, scalperUserConfig.minOrderBookSellSolForStop]);

  const filteredLogs = useMemo(() => {
    if (logFilter === "all") return commentaryLogs;
    return commentaryLogs.filter((log) => {
      const lower = log.toLowerCase();
      if (logFilter === "brain") {
        return (
          lower.includes("brain") ||
          lower.includes("neural") ||
          lower.includes("sensory") ||
          lower.includes("stress") ||
          lower.includes("synap") ||
          lower.includes("pulse") ||
          lower.includes("stream")
        );
      }
      if (logFilter === "motor") {
        return (
          lower.includes("motor") ||
          lower.includes("decision") ||
          lower.includes("circuit") ||
          lower.includes("risk") ||
          lower.includes("latch")
        );
      }
      if (logFilter === "trades") {
        return (
          lower.includes("trade") ||
          lower.includes("position") ||
          lower.includes("exec") ||
          lower.includes("buy") ||
          lower.includes("sell") ||
          lower.includes("dump") ||
          lower.includes("pyramid")
        );
      }
      if (logFilter === "vision") {
        return (
          lower.includes("vision") ||
          lower.includes("setup") ||
          lower.includes("focus") ||
          lower.includes("dip") ||
          lower.includes("support") ||
          lower.includes("bounce") ||
          lower.includes("flow") ||
          lower.includes("candle")
        );
      }
      return true;
    });
  }, [commentaryLogs, logFilter]);

  const formatLog = (log: string) => {
    const regex = /^\[(.*?)\]\s*(.*?):\s*(.*)$/;
    const match = log.match(regex);
    if (!match) return <span className="text-white/50 text-[10px] font-mono">{log}</span>;
    const [_, timestamp, category, message] = match;
    let categoryColor = "text-emerald-400 font-bold";
    let badgeBg = "bg-emerald-500/10 border-emerald-500/20";

    const catLower = category.toLowerCase();
    if (catLower.includes("vision") || catLower.includes("setup") || catLower.includes("focus")) {
      categoryColor = "text-sky-400 font-bold";
      badgeBg = "bg-sky-500/10 border-sky-500/20";
    } else if (catLower.includes("motor") || catLower.includes("decision")) {
      categoryColor = "text-amber-400 font-bold";
      badgeBg = "bg-amber-500/10 border-amber-500/20";
    } else if (catLower.includes("risk") || catLower.includes("circuit") || catLower.includes("stress") || catLower.includes("dump") || catLower.includes("rug") || catLower.includes("whale")) {
      categoryColor = "text-rose-400 font-bold";
      badgeBg = "bg-rose-500/10 border-rose-500/20";
    } else if (catLower.includes("trade") || catLower.includes("position") || catLower.includes("exec")) {
      categoryColor = "text-purple-400 font-bold";
      badgeBg = "bg-purple-500/10 border-purple-500/20";
    } else if (catLower.includes("flow") || catLower.includes("telemetry") || catLower.includes("candle")) {
      categoryColor = "text-cyan-400 font-bold";
      badgeBg = "bg-cyan-500/10 border-cyan-500/20";
    } else if (catLower.includes("stream")) {
      categoryColor = "text-zinc-400 font-bold";
      badgeBg = "bg-white/5 border-white/10";
    }

    return (
      <div className="flex items-start gap-2 py-0.5 border-b border-white/[0.03] hover:bg-white/[0.04] px-1.5 rounded transition-colors group">
        <span className="text-[9px] font-mono text-white/30 shrink-0 select-none w-14 pt-0.5">{timestamp}</span>
        <span className={`text-[8px] font-mono shrink-0 uppercase tracking-wider px-1.5 py-0.5 rounded border ${categoryColor} ${badgeBg} max-w-[88px] truncate text-center`}>
          {category}
        </span>
        <span className="text-[10px] font-mono text-white/80 leading-relaxed break-all group-hover:text-white transition-colors">
          {message}
        </span>
      </div>
    );
  };

  const pumpCfgRev = usePumpPortalConfigRevision();
  const tradingWalletPk = useMemo(() => getPumpPortalTradingWalletPubkey(), [pumpCfgRev]);
  const { sol: tradingWalletSol, loading: tradingWalletSolLoading } = usePumpPortalLinkedWalletSol(tradingWalletPk);
  const [walletCopied, setWalletCopied] = useState(false);
  const [gameMode, setGameMode] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);

  const handleCopyWallet = () => {
    if (tradingWalletPk) {
      navigator.clipboard.writeText(tradingWalletPk);
      setWalletCopied(true);
      setTimeout(() => setWalletCopied(false), 2000);
    }
  };

  const toggleGameMode = () => {
    setGameMode(!gameMode);
    if (!gameMode) {
      // Initialize agent wallets
      const wallet = agentWalletManager.createAgentWallet();
      agentWalletManager.activateWallet(wallet.id);
    }
  };

  const [tradingNotice, setTradingNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!tradingNotice) return;
    const tick = window.setTimeout(() => setTradingNotice(null), 5200);
    return () => window.clearTimeout(tick);
  }, [tradingNotice]);

  const sessionStripError = tradingMode === "real" && algoSessionActive && chartAnalytics.livePumpPortalLastErr;
  const currentTrade = chartAnalytics?.paperScalper?.currentTrade;

  // Bounce zones for current mint

  const stats = useMemo(() => {
    const today = Date.now() - 24 * 60 * 60 * 1000;
    const trades = (persistedBotTrades || []).filter((t: any) => t.closedAtTs > today);
    let wins = 0;
    let netSol = 0;
    trades.forEach((t: any) => {
      const isWin = t.kind === "chain" ? t.netSol > 0 : (t.paperSolEstimate?.netSol || 0) > 0 || t.pnlPct > 0;
      if (isWin) wins++;
      netSol += t.kind === "chain" ? t.netSol : t.paperSolEstimate?.netSol || 0;
    });
    return {
      count: trades.length,
      winRate: trades.length > 0 ? Math.round((wins / trades.length) * 100) : 0,
      netSol,
    };
  }, [persistedBotTrades]);

  // Scalper status helpers
  const scalperStatus = chartAnalytics.paperScalper?.status as string | undefined;
  const statusLabel =
    scalperStatus === "watching" ? "Watching" :
      scalperStatus === "dip" ? "Dipped — zone scan" :
        scalperStatus === "nearing" ? "Nearing bounce zone" :
          scalperStatus === "arming" ? `Armed — awaiting ${scalperUserConfig.catalystMinSol}+ SOL` :
            scalperStatus === "in_trade" ? "In Trade" :
              algoSessionActive ? "Running" : "Idle";

  const statusColor =
    scalperStatus === "arming" ? "text-emerald-400" :
      scalperStatus === "in_trade" ? "text-purple-400" :
        scalperStatus === "nearing" ? "text-amber-400" :
          "text-white/50";

  return (
    <div className="flex h-full flex-col bg-neutral-900 border-t border-white/5 overflow-hidden font-sans">
      {/* ── Header ── */}
      <div className="flex items-center justify-between shrink-0 px-4 py-2.5 bg-neutral-900 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Target className="size-3.5 text-emerald-400" />
          <h3 className="text-[11px] font-black uppercase tracking-widest text-white">Agent</h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Game Mode Toggle */}
          <button
            onClick={toggleGameMode}
            className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-wider transition-all ${gameMode
                ? "bg-purple-500/20 border-purple-500/40 text-purple-400"
                : "bg-white/5 border-white/10 text-white/30"
              }`}
          >
            <Bolt className="size-2.5" />
            Game
          </button>

          {/* Wallet Balance */}
          {tradingWalletPk && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] font-semibold tabular-nums text-emerald-200">
                {tradingWalletSolLoading ? (
                  <Loader2 className="size-2.5 animate-spin" />
                ) : tradingWalletSol === null ? (
                  <span className="text-rose-400">Error</span>
                ) : (
                  <>
                    <span>{tradingWalletSol.toFixed(4)} SOL</span>
                    <span className="opacity-40">/</span>
                    <span className="text-white/80">${(tradingWalletSol * (chartAnalytics.solPrice || 150)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </>
                )}
              </div>
              <button
                onClick={handleCopyWallet}
                className="flex items-center justify-center rounded-full p-1 hover:bg-white/5 transition-colors"
                title="Copy wallet address"
              >
                {walletCopied ? (
                  <Check className="size-2.5 text-emerald-400" />
                ) : (
                  <Copy className="size-2.5 text-white/40 hover:text-white/60 transition-colors" />
                )}
              </button>
            </div>
          )}
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-wider ${algoSessionActive
              ? tradingMode === "real"
                ? sessionStripError
                  ? "bg-red-500/10 border-red-500/30 text-red-400"
                  : "bg-amber-500/10 border-amber-500/30 text-amber-400"
                : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
              : "bg-white/5 border-white/10 text-white/30"
            }`}>
            <Activity className={`size-2.5 ${algoSessionActive ? "animate-pulse" : ""}`} />
            {algoSessionActive ? (tradingMode === "real" ? "REAL ACTIVE" : "PAPER ACTIVE") : "IDLE"}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-3 space-y-3">

          {/* ── Pre-Entry Radar ── */}
          {serverMomentumWatchlist && serverMomentumWatchlist.length > 0 && (
            <div className="flex flex-col gap-2.5 mb-3 bg-gradient-to-r from-emerald-500/[0.04] via-transparent to-black/30 p-3 rounded-2xl border border-emerald-500/10 shadow-lg">
              <div className="flex items-center justify-between">
                <h3 className="text-[11px] font-black uppercase tracking-widest text-emerald-400 pl-1 flex items-center gap-2">
                  <TrendingUp className="size-3.5 text-emerald-400 animate-pulse" /> Pre-Entry Radar
                </h3>
                <span className="text-[10px] font-mono font-bold text-white/40 bg-white/5 px-2 py-0.5 rounded-full border border-white/5">
                  {serverMomentumWatchlist.length} Live Targets
                </span>
              </div>
              <div className="flex flex-col gap-2 overflow-y-auto max-h-[300px] custom-scrollbar pr-1 pt-1">
                {serverMomentumWatchlist.map((r: any) => (
                  <div key={r.mint} className="relative group">
                    <RunnerRow 
                      token={r} 
                      isSelected={caMintInput === r.mint} 
                      onOpen={(mint) => {
                        setCaMintInput(mint);
                        if (isMobile && setMobileActiveTab) setMobileActiveTab("chart");
                      }} 
                    />
                    <div className="absolute top-2 right-2 z-10 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <span className={`text-[10px] px-2 py-1 rounded-full font-mono font-black ${r.waitReason ? 'bg-amber-500/90 text-black shadow-[0_0_10px_rgba(245,158,11,0.5)]' : 'bg-emerald-500/90 text-black shadow-[0_0_10px_rgba(16,185,129,0.5)]'}`}>
                        {r.roc1m > 0 ? '+' : ''}{(r.roc1m * 100).toFixed(1)}% RoC
                      </span>
                      {r.waitReason && (
                        <span className="text-[9px] px-2 py-1 rounded-full font-mono font-bold bg-black/80 text-amber-400 border border-amber-500/30 whitespace-nowrap">
                          {r.waitReason}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── FlyBrain Neural OS HUD ─────────────────────────────────────── */}
          {!isMobile && <FlyBrainNurseryHUD />}

          {/* ── Agent Controls (Compact) ── */}
          <div className="rounded-xl border border-white/10 bg-gradient-to-b from-neutral-800 to-neutral-900 shadow-xl relative p-3">
            {algoSessionActive && (
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-500/40 to-transparent" />
            )}

            {/* HEADER: Target CA + Mode + Action */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex-1 flex items-center justify-between gap-2 bg-black/40 rounded-lg px-2.5 py-1.5 border border-white/5 overflow-hidden">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`shrink-0 size-1.5 rounded-full ${scalperStatus === "in_trade" ? "bg-purple-400 animate-pulse" : scalperStatus === "arming" ? "bg-emerald-400 animate-ping" : algoSessionActive ? "bg-white/30 animate-pulse" : "bg-white/10"}`} />
                  <span className="text-[10px] font-mono text-white/50 truncate">
                    {caMintInput ? caMintInput : "No target token"}
                  </span>
                </div>
                <span className={`text-[9px] font-black uppercase tracking-wider shrink-0 ${statusColor}`}>
                  {statusLabel}
                </span>
              </div>
              
              <button onClick={() => setConfigExpanded(!configExpanded)} className={`flex items-center justify-center p-1.5 rounded-lg border transition-colors ${configExpanded ? "bg-sky-500/10 border-sky-500/30 text-sky-400" : "bg-white/5 border-white/5 text-white/40 hover:text-white/80"}`} title="Toggle Configuration">
                <Settings2 className="size-3.5" />
              </button>

              <div className="flex bg-black/60 rounded-lg border border-white/5 p-0.5 shrink-0">
                <button onClick={() => !algoSessionActive && setTradingMode("paper")} disabled={algoSessionActive} className={`px-2 py-1 rounded text-[8px] font-black uppercase ${tradingMode === "paper" ? "bg-white/10 text-white" : "text-white/25 hover:text-white/50"}`}>Paper</button>
                <button onClick={() => !algoSessionActive && setTradingMode("real")} disabled={algoSessionActive} className={`px-2 py-1 rounded text-[8px] font-black uppercase ${tradingMode === "real" ? "bg-rose-500/20 text-rose-300" : "text-white/25 hover:text-white/50"}`}>Real</button>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (algoSessionActive) { hardStopTrading(); }
                  else {
                    if (!caMintInput) return;
                    if (tradingMode === "real" && !getEffectivePumpPortalApiKey().trim()) {
                      setTradingNotice(appendPumpPortalTradingWalletHint("PumpPortal API key missing."));
                      return;
                    }
                    setTradingHalted(false); setAlgoSessionActive(true);
                  }
                }}
                disabled={!algoSessionActive && !caMintInput}
                className={`px-3 py-1.5 shrink-0 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center gap-1.5 transition-all ${algoSessionActive ? "bg-rose-500/15 text-rose-400 border border-rose-500/40 hover:bg-rose-500/25" : !caMintInput ? "bg-white/5 text-white/15 border border-transparent cursor-not-allowed" : "bg-emerald-600 text-white border border-emerald-500/60 hover:bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.2)]"}`}
              >
                {algoSessionActive ? <><Square className="size-2.5 fill-current" /> Stop</> : <><Play className="size-2.5 fill-current" /> Run</>}
              </button>
            </div>

            {tradingNotice && <p className="text-[9px] text-amber-400 bg-amber-500/8 px-2 py-1.5 mb-2 rounded border border-amber-500/20">{tradingNotice}</p>}

            {/* COMPACT CONFIG GRID */}
            {configExpanded && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 p-1.5 bg-black/30 rounded-lg border border-white/5">
              <AgentKnob label="Entry Size" value={scalperLiveBuySol} min={0.001} max={25} step={0.001} unit="SOL" onChange={(v) => setScalperLiveBuySol(v)} />
              <AgentKnob label="Catalyst Vol" value={scalperUserConfig.catalystMinSol ?? 0.1} min={0.01} max={10} step={0.01} unit="SOL" onChange={(v) => setScalperUserConfig({ catalystMinSol: v })} />
              
              <AgentKnob label="Whale Entry" value={scalperUserConfig.minWhaleSol ?? 1.0} min={0.1} max={10} step={0.1} unit="SOL" accent="amber" onChange={(v) => setScalperUserConfig({ minWhaleSol: v })} />
              <AgentKnob label="RoC Limit" value={scalperUserConfig.minRoc1m ?? 0.1} min={0.01} max={1.0} step={0.01} unit="%" accent="sky" onChange={(v) => setScalperUserConfig({ minRoc1m: v })} />

              <AgentKnob label="Take Profit" value={scalperUserConfig.takeProfitPct ?? 10} min={1} max={1000} step={1} unit="%" accent="emerald" onChange={(v) => setScalperUserConfig({ takeProfitPct: v })} />
              <AgentKnob label="Dip Entry" value={scalperUserConfig.dipMinPct ?? 4} min={1} max={90} step={1} unit="%" accent="sky" onChange={(v) => setScalperUserConfig({ dipMinPct: v })} />
              
              <AgentKnob label="Sell Stop" value={scalperUserConfig.minOrderBookSellSolForStop ?? 0.2} min={0.01} max={10} step={0.01} unit="SOL" accent="rose" onChange={(v) => setScalperUserConfig({ minOrderBookSellSolForStop: v })} />
              <AgentKnob label="Cooldown" value={Math.round((scalperUserConfig.reentryCooldownMs ?? 30000) / 1000)} min={1} max={300} step={1} unit="s" onChange={(v) => setScalperUserConfig({ reentryCooldownMs: Math.round(v) * 1000 })} />
              
              {tradingMode === "real" && (
                <>
                  <AgentKnob label="Slippage" value={scalperUserConfig.realSlippagePct ?? 15} min={1} max={50} step={1} unit="%" accent="amber" onChange={(v) => setScalperUserConfig({ realSlippagePct: v })} />
                  <AgentKnob label="Priority Fee" value={scalperUserConfig.realPriorityFeeSol ?? 0.001} min={0.00001} max={0.1} step={0.0001} unit="SOL" accent="amber" onChange={(v) => setScalperUserConfig({ realPriorityFeeSol: v })} />
                </>
              )}
              
              {/* Mega Filter */}
              <div className="col-span-2 mt-1 border-t border-white/5 pt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
                <div className="col-span-2 flex items-center justify-between px-1 mb-0.5">
                  <span className="text-[9px] font-black uppercase tracking-widest text-purple-400">Mega Filter / Radar</span>
                  <button 
                    onClick={() => setScalperUserConfig({ megaFilterEnabled: !scalperUserConfig.megaFilterEnabled })}
                    className={`px-2 py-0.5 rounded text-[8px] font-black uppercase transition-all ${
                      scalperUserConfig.megaFilterEnabled 
                        ? "bg-purple-500/20 text-purple-300 border border-purple-500/30"
                        : "bg-white/5 text-white/30 border border-white/5"
                    }`}
                  >
                    {scalperUserConfig.megaFilterEnabled ? "Enabled" : "Off"}
                  </button>
                </div>
                {scalperUserConfig.megaFilterEnabled && (
                  <>
                    <AgentKnob label="Max Age" value={scalperUserConfig.filterMaxAgeHours ?? 48} min={1} max={168} step={1} unit="h" onChange={(v) => setScalperUserConfig({ filterMaxAgeHours: v })} />
                    <AgentKnob label="Min Liquidity" value={scalperUserConfig.filterMinLiqUsd ?? 30000} min={1000} max={500000} step={1000} unit="$" accent="emerald" onChange={(v) => setScalperUserConfig({ filterMinLiqUsd: v })} />
                    <AgentKnob label="Min 24h Buys" value={scalperUserConfig.filterMinBuys24h ?? 300} min={10} max={5000} step={10} unit="buys" accent="amber" onChange={(v) => setScalperUserConfig({ filterMinBuys24h: v })} />
                    <AgentKnob label="Top 10 Max" value={scalperUserConfig.filterMaxTop10Pct ?? 40} min={10} max={90} step={1} unit="%" accent="rose" onChange={(v) => setScalperUserConfig({ filterMaxTop10Pct: v })} />
                  </>
                )}
              </div>
            </div>
            )}

            {/* Wallet (Real Mode) */}
            {tradingWalletPk && tradingMode === "real" && (
              <div className="mt-2.5 bg-black/30 px-2.5 py-1.5 rounded flex justify-between items-center border border-white/5">
                <div className="flex items-center gap-2">
                  <span className="text-[8px] font-black uppercase text-white/40">Wallet</span>
                  <span className="text-[9px] font-mono text-emerald-400/80">{tradingWalletSolLoading ? "..." : `${tradingWalletSol?.toFixed(4) || "0.0000"} SOL`}</span>
                </div>
                <button onClick={handleCopyWallet} className="text-[8px] uppercase font-black text-white/30 hover:text-white/60 transition-colors flex items-center gap-1">
                  {walletCopied ? <><Check className="size-2 text-emerald-400" /> Copied</> : <><Copy className="size-2" /> Copy PK</>}
                </button>
              </div>
            )}

            {/* Bounce Zones Removed */}
          </div>

          <AiCommentaryPanel />

          <ServerBotTrades />



          {/* ── Active Trade Banner ── */}
          {currentTrade && (
            <div className="rounded-xl border border-purple-500/30 bg-purple-500/8 p-3 shadow-[0_0_20px_rgba(168,85,247,0.08)]">
              <div className="flex items-center justify-between">
                <div>
                  <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider text-purple-400 mb-1">
                    <span className="size-1.5 rounded-full bg-purple-400 animate-ping" />
                    Open Position
                  </span>
                  <p className="text-[11px] font-bold text-white">
                    Following {(recommendations.find(r => r.mint === caMintInput)?.coin || watchdog.revivals.find(r => r.mint === caMintInput)?.symbol || watchdog.multipliers.find(m => m.mint === caMintInput)?.symbol || (caMintInput ? caMintInput.slice(0, 6) : "Target"))} Whale Catalyst {currentTrade.catalystSol ? `(${currentTrade.catalystSol.toFixed(1)} SOL trigger)` : ""}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`text-lg font-mono font-black ${currentTrade.unrealizedPct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {currentTrade.unrealizedPct >= 0 ? "+" : ""}{currentTrade.unrealizedPct.toFixed(2)}%
                  </span>
                  <div className="flex gap-2 text-[9px] font-mono text-white/40 justify-end mt-0.5">
                    <span>Entry: ${Math.round(currentTrade.entryMcUsd).toLocaleString()}</span>
                    <span>Now: ${currentTrade.lastMcUsd ? Math.round(currentTrade.lastMcUsd).toLocaleString() : "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Emergency Sell ── */}
          {algoSessionActive && tradingMode === "real" && chartAnalytics.paperScalper?.status === "in_trade" && (
            <button
              type="button"
              onClick={() => requestManualSell()}
              className="w-full h-11 rounded-xl border border-rose-500/60 bg-rose-600/15 text-rose-300 text-[10px] font-black uppercase tracking-widest transition-all hover:bg-rose-600/25 hover:border-rose-500 shadow-[0_0_15px_rgba(244,63,94,0.1)]"
            >
              Emergency Sell All Position
            </button>
          )}

          {/* ── Live Data Ticker ── */}
          {(() => {
            const tape = chartAnalytics.tapeSummary;
            const candle = chartAnalytics.lastCandle;
            const snap = chartAnalytics.paperScalper;
            const mc = tape?.latestMcUsd;
            const mcFmt = mc != null
              ? mc >= 1_000_000 ? `$${(mc / 1_000_000).toFixed(2)}M` : `$${(mc / 1_000).toFixed(1)}k`
              : "—";
            const buyPct = tape?.buyPct ?? null;
            const vol = tape?.solVolume ?? null;
            const candleDir = candle ? (candle.close >= candle.open ? 1 : -1) : 0;
            const candlePct = candle && candle.open > 0
              ? (((candle.close - candle.open) / candle.open) * 100)
              : null;
            // Last few prints as mini flow bars
            const prints = tape?.recentPrints?.slice(-8) ?? [];
            return (
              <div className="bg-neutral-900 border border-white/8 rounded-xl p-3 space-y-2.5">
                {/* Top row: MC + candle + buy pressure */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[8px] font-black uppercase tracking-widest text-white/25">Market Cap</span>
                    <span className="text-[13px] font-mono font-black text-white">{mcFmt}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[8px] font-black uppercase tracking-widest text-white/25">Last Candle</span>
                    <span className={`text-[13px] font-mono font-black ${candleDir >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {candlePct != null ? `${candlePct >= 0 ? "+" : ""}${candlePct.toFixed(2)}%` : "—"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[8px] font-black uppercase tracking-widest text-white/25">Buy Pressure</span>
                    <span className={`text-[13px] font-mono font-black ${buyPct != null && buyPct >= 60 ? "text-emerald-400" : buyPct != null && buyPct < 40 ? "text-rose-400" : "text-white/70"}`}>
                      {buyPct != null ? `${buyPct.toFixed(0)}%` : "—"}
                    </span>
                  </div>
                </div>

                {/* Second row: Counts + volume + latch state */}
                <div className="flex items-center gap-3 flex-wrap">
                  {tape && (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="text-[8px] font-black text-emerald-400">{tape.buyCount}B</span>
                        <span className="text-white/15 text-[8px]">/</span>
                        <span className="text-[8px] font-black text-rose-400">{tape.sellCount}S</span>
                        <span className="text-[8px] text-white/25 ml-1">trades</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[8px] font-mono text-amber-400">{vol != null ? `${vol.toFixed(3)} SOL` : "—"}</span>
                        <span className="text-[8px] text-white/25">vol</span>
                      </div>
                    </>
                  )}
                  {snap && (
                    <div className={`ml-auto flex items-center gap-1 px-2 py-0.5 rounded border text-[8px] font-black uppercase tracking-wider ${snap.status === "arming" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400" :
                        snap.status === "in_trade" ? "bg-purple-500/10 border-purple-500/30 text-purple-400" :
                          snap.status === "nearing" ? "bg-amber-500/10 border-amber-500/30 text-amber-400" :
                            snap.status === "dip" ? "bg-sky-500/10 border-sky-500/30 text-sky-400" :
                              "bg-white/5 border-white/10 text-white/30"
                      }`}>
                      <span className={`size-1.5 rounded-full ${snap.status === "arming" ? "bg-emerald-400 animate-ping" :
                          snap.status === "in_trade" ? "bg-purple-400 animate-pulse" :
                            snap.status === "nearing" ? "bg-amber-400 animate-pulse" :
                              "bg-white/20"
                        }`} />
                      {snap.status.replace("_", " ")}
                    </div>
                  )}
                </div>

                {/* Buy/sell flow bars (mini tape visualizer) */}
                {prints.length > 0 && (
                  <div className="flex items-end gap-0.5 h-6">
                    {prints.map((p: any, i: number) => {
                      const h = Math.min(100, Math.max(15, (p.sol / 2) * 100));
                      return (
                        <div
                          key={i}
                          className={`flex-1 rounded-sm transition-all ${p.buy ? "bg-emerald-500/60" : "bg-rose-500/60"}`}
                          style={{ height: `${h}%` }}
                          title={`${p.buy ? "Buy" : "Sell"}: ${p.sol?.toFixed(3)} SOL`}
                        />
                      );
                    })}
                    <span className="text-[8px] text-white/20 ml-1 self-center shrink-0">tape</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── FlyBrain Neural Terminal ── */}
          <div className="flex flex-col h-[320px] rounded-xl border border-emerald-500/20 bg-neutral-950 overflow-hidden shadow-[0_0_25px_rgba(16,185,129,0.04)]">
            <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 bg-neutral-900/90 backdrop-blur shrink-0">
              <div className="flex items-center gap-2">
                <div className="relative flex items-center justify-center size-5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                  <BrainCircuit className="size-3 animate-pulse" />
                  <span className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-emerald-400 animate-ping" />
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-black uppercase text-white/90 tracking-wider">FlyBrain Logs</span>
                    <span className={`text-[7.5px] font-mono px-1.5 py-0.2 rounded font-bold ${flyBrainState?.connected ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25" : "bg-white/5 text-white/40 border border-white/10"}`}>
                      {flyBrainState?.connected ? "● LIVE" : "○ SYNC"}
                    </span>
                  </div>
                  <span className="text-[8px] font-mono text-white/40">
                    {flyBrainState?.spikeRate ?? 0} Hz · {flyBrainState?.watchedTokens ?? 0} mkts · Dopamine: {(flyBrainState?.dopamine ?? 0) >= 0 ? "+" : ""}{(flyBrainState?.dopamine ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {(["all", "brain", "motor", "trades", "vision"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setLogFilter(f)}
                    className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-wider transition-all ${logFilter === f
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.15)]"
                        : "text-white/30 hover:text-white/60 border border-transparent"
                      }`}
                  >
                    {f}
                  </button>
                ))}
                {commentaryLogs.length > 0 && (
                  <button
                    onClick={() => setCommentaryLogs([])}
                    title="Clear logs"
                    className="ml-1 p-1 text-white/20 hover:text-rose-400 transition-colors"
                  >
                    <Trash2 className="size-2.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-px flex flex-col-reverse">
              <div className="space-y-px">
                {filteredLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-1.5">
                    <BrainCircuit className="size-5 text-emerald-500/30 animate-pulse" />
                    <p className="text-[9px] text-white/30 font-mono">Awaiting FlyBrain neural events…</p>
                  </div>
                ) : (
                  [...filteredLogs].reverse().map((log, idx) => (
                    <div key={`${log}-${idx}`}>{formatLog(log)}</div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ── Performance Stats ── */}
          <div className="border-t border-white/5 pt-4 space-y-3">
            <h4 className="text-[9px] font-black uppercase tracking-widest text-white/30">Performance · 24h</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-neutral-900 p-3 border border-white/5 text-center">
                <p className="text-[8px] font-black text-white/30 uppercase tracking-widest mb-1">Win Rate</p>
                <p className="text-base font-mono font-black text-white">{stats.winRate}%</p>
              </div>
              <div className="rounded-xl bg-neutral-900 p-3 border border-white/5 text-center">
                <p className="text-[8px] font-black text-white/30 uppercase tracking-widest mb-1">Trades</p>
                <p className="text-base font-mono font-black text-white">{stats.count}</p>
              </div>
              <div className="rounded-xl bg-neutral-900 p-3 border border-white/5 text-center">
                <p className="text-[8px] font-black text-white/30 uppercase tracking-widest mb-1">PnL</p>
                <p className={`text-base font-mono font-black ${stats.netSol >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                  {stats.netSol >= 0 ? "+" : ""}{stats.netSol.toFixed(3)}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <h4 className="text-[9px] font-black uppercase tracking-widest text-white/30">Execution History</h4>
              {(persistedBotTrades?.length ?? 0) > 0 && (
                <button
                  onClick={() => window.confirm("Clear all persisted execution logs?") && clearPersistedTrades()}
                  className="flex items-center gap-1 text-[8px] font-black uppercase text-rose-400/60 hover:text-rose-400 transition-colors"
                >
                  <Trash2 className="size-2.5" /> Clear
                </button>
              )}
            </div>

            {/* Open Position */}
            {chartAnalytics.paperScalper?.status === "in_trade" && (
              <div className="mb-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <h5 className="text-[9px] font-black uppercase tracking-widest text-emerald-300">Open Position</h5>
                </div>
                <div className="text-[10px] font-mono text-white/70 flex items-center gap-1">
                  <span className="animate-casino-flash font-bold text-emerald-400">Entry MC: {formatMcUsdBookDetail(chartAnalytics.paperScalper.entryMcUsd || 0)}</span>{' '}
                  {(chartAnalytics.paperScalper as any).ath ? <span className="animate-casino-flash font-bold text-emerald-400">✨ ATH: {formatMcUsdBookDetail((chartAnalytics.paperScalper as any).ath)}</span> : ''}
                  <GainBadge currentMc={chartAnalytics.paperScalper.entryMcUsd} spawnMc={(chartAnalytics.paperScalper as any).spawnMc} priceChange24h={(chartAnalytics.paperScalper as any).priceChange24h} />
                </div>
              </div>
            )}

            <div className="bg-neutral-900 border border-white/5 rounded-xl overflow-hidden">
              {tradingMode === "real" ? (
                <BotTradesBook rows={persistedBotTrades.filter((t: any) => t.kind === "chain")} emptyHint="No on-chain round-trips yet." />
              ) : (
                <BotTradesBook rows={persistedBotTrades.filter((t: any) => t.kind === "tape")} emptyHint="No paper trades yet. Start the agent." paperMode />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
